import { EventEmitter, FactoryProvider } from '@angular/core';
import { PDAuthService } from 'pdauth';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { util } from '../../classes/util';

const NgAuthSleepHackTime = 500;

type IUserSettingsServiceConstructor = new () => IUserSettingsService;
interface IUserSettingsService {
    userSettingsChange;
    GetSettingValue: (settingName: string, appName: string, userEmail: string) => Observable<any>;
    SetSettingValue: (settingName: string, value: any, userEmail: string, appName: string) => Promise<any>;
    ClearSettingValue: (settingName: string, userEmail: string, appName: string) => Promise<any>;
}

class UserSettingsService implements IUserSettingsService {

    public userSettingsChange = new EventEmitter<any>();
    constructor(private readonly pdauth: PDAuthService) { }

    GetSettingValue(settingName: string, appName: string, userEmail: string) {
        return this.pdauth.getUserSettings(null, appName, userEmail).pipe(map(val => {
            let retVal = null;
            if (val && val[settingName] !== undefined) {
                retVal = val[settingName];
            }
            return retVal;
        }));
    }

    SetSettingValue(settingName: string, value: any, userEmail: string, appName: string) {
        return new Promise<void>((resolve, _reject) => {
            this.pdauth.getUserSettings(null, appName, userEmail).pipe(first()).subscribe(userSettings => {
                let retVal = {};
                if (userSettings) {
                    retVal = userSettings;
                }
                retVal[settingName] = value;
                // This method doesn't resolve the promise it returns properly, so we're going to sleep for half a second
                // This should be updated once that method is fixed
                this.pdauth.replaceUserSettings(retVal, userEmail, appName);
                util.sleep(NgAuthSleepHackTime).then(resolve);
                this.userSettingsChange.emit({ settingName, value, userEmail, appName });
            });
        });
    }

    async ClearSettingValue(settingName: string, userEmail: string, appName: string) {
        return this.pdauth.getUserSettings(null, appName, userEmail).pipe(switchMap(userSettings => {
            let retVal = {};
            if (userSettings) {
                retVal = userSettings;
                if (retVal[settingName] !== undefined) {
                    // tslint:disable-next-line:no-dynamic-delete
                    delete retVal[settingName];
                }
            }
            return this.pdauth.updateUserSettings(retVal, userEmail, appName);
        }));
    }
}

const UserSettingsServiceProvider: FactoryProvider = {
    deps: [PDAuthService],
    provide: 'UserSettingsService',
    useFactory: (pdauth: PDAuthService) => window.UserSettingsService ? new window.UserSettingsService() : new UserSettingsService(pdauth)
};

export { UserSettingsServiceProvider, IUserSettingsService, IUserSettingsServiceConstructor };
